/**
 * treadmillVideoExporter.ts
 *
 * Client-side exporter that converts a Treadmill slideshow into a downloadable
 * video file (.mp4 / .webm) using HTML5 Canvas, Web Audio API, and MediaRecorder.
 *
 * ⚡ 100% Client-Side — ZERO server load!
 * 🚀 High-speed real-time canvas rendering with 100% accurate progress & full slideshow recording.
 */
import type { TreadmillItem } from '@/api/treadmills.api';

export interface ExportProgress {
  currentSlide: number;
  totalSlides: number;
  percent: number;
  statusText?: string;
}

export interface ExportOptions {
  items: TreadmillItem[];
  treadmillName?: string;
  onProgress?: (progress: ExportProgress) => void;
  signal?: AbortSignal;
  width?: number;
  height?: number;
  fps?: number;
}

/** Helper to clean HTML tags from rich text strings */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

/** Helper to measure and wrap text into multiple lines for canvas rendering */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const lines: string[] = [];
  const rawParagraphs = text.split('\n');

  for (const para of rawParagraphs) {
    if (!para.trim()) {
      lines.push('');
      continue;
    }
    const words = para.split(' ');
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
  }
  return lines;
}

/** Load an image element asynchronously */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = () => resolve(img2);
      img2.src = src;
    };
    img.src = src;
  });
}

/** Preload video element until buffered enough to play smoothly */
function preloadVideo(src: string): Promise<HTMLVideoElement> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.crossOrigin = 'anonymous';
    v.preload = 'auto';
    v.muted = false;
    v.playsInline = true;

    const onCanPlay = () => resolve(v);
    v.addEventListener('canplaythrough', onCanPlay, { once: true });
    v.addEventListener('canplay', onCanPlay, { once: true });
    v.onerror = () => resolve(v);
    v.src = src;
    v.load();
    setTimeout(() => resolve(v), 3000);
  });
}

/** Normalize playback rate from legacy 10-scale or new 100-scale */
function parseRate(rate?: number): number {
  if (rate === undefined || rate === null || isNaN(rate)) return 1.0;
  if (rate <= 20) return (rate <= 0 ? 10 : rate) / 10;
  return rate / 100;
}

/**
 * Main export function.
 * Renders all slides in real-time sequence to a Canvas and AudioDestinationNode,
 * recording the stream into a video Blob and triggering browser download.
 */
export async function exportTreadmillToVideo(options: ExportOptions): Promise<void> {
  const {
    items,
    treadmillName = 'Тредмил',
    onProgress,
    signal,
    width = 1280,
    height = 720,
    fps = 30,
  } = options;

  if (!items || items.length === 0) {
    throw new Error('Нет слайдов для экспорта');
  }

  const slides = items;
  const totalSlides = slides.length;
  const totalDurationSec = slides.reduce((sum, s) => sum + Math.max(0.5, s.duration ?? 4), 0);

  onProgress?.({
    currentSlide: 0,
    totalSlides,
    percent: 0,
    statusText: 'Подготовка и предзагрузка медиафайлов...',
  });

  // Setup Canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Не удалось создать контекст Canvas 2D');

  // Preload all assets upfront
  const loadedImages = new Map<string, HTMLImageElement>();
  const loadedVideos = new Map<string, HTMLVideoElement>();

  for (let i = 0; i < slides.length; i++) {
    if (signal?.aborted) throw new Error('Экспорт отменён пользователем');

    const s = slides[i]!;
    if (s.imageSrc && !loadedImages.has(s.imageSrc)) {
      const img = await loadImage(s.imageSrc);
      loadedImages.set(s.imageSrc, img);
    }
    if (s.videoSrc && !loadedVideos.has(s.videoSrc)) {
      const vid = await preloadVideo(s.videoSrc);
      loadedVideos.set(s.videoSrc, vid);
    }

    onProgress?.({
      currentSlide: 0,
      totalSlides,
      percent: Math.round(((i + 1) / totalSlides) * 10),
      statusText: `Загрузка ресурсов ${i + 1} из ${totalSlides}...`,
    });
  }

  // Setup Audio Context for Web Audio API mixing
  const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
  const audioCtx = new AudioCtxClass();
  const audioDest = audioCtx.createMediaStreamDestination();

  // Capture Canvas stream
  const canvasStream = canvas.captureStream(fps);
  const combinedStream = new MediaStream();

  // Add video track
  canvasStream.getVideoTracks().forEach(track => combinedStream.addTrack(track));
  // Add audio track
  audioDest.stream.getAudioTracks().forEach(track => combinedStream.addTrack(track));

  // Select supported MediaRecorder MIME type
  const mimeTypes = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  const selectedMime = mimeTypes.find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';

  const recorder = new MediaRecorder(combinedStream, {
    mimeType: selectedMime,
    videoBitsPerSecond: 5_000_000, // 5 Mbps HD
  });

  const chunks: Blob[] = [];
  recorder.ondataavailable = e => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  recorder.start(100);

  const frameIntervalMs = 1000 / fps;
  let accumulatedTimeSec = 0;

  try {
    for (let i = 0; i < slides.length; i++) {
      if (signal?.aborted) throw new Error('Экспорт отменён пользователем');

      const slide = slides[i]!;
      const slideDurationSec = Math.max(0.5, slide.duration ?? 4);

      const loadedImg = slide.imageSrc ? loadedImages.get(slide.imageSrc) ?? null : null;
      const videoNode = slide.videoSrc ? loadedVideos.get(slide.videoSrc) ?? null : null;

      // Setup audio playback if present
      let audioNode: HTMLAudioElement | null = null;
      const audioUrl = slide.audioSrc || slide.ttsAudio;
      if (audioUrl) {
        try {
          audioNode = new Audio();
          audioNode.crossOrigin = 'anonymous';
          audioNode.src = audioUrl;
          audioNode.volume = (slide.audioVolume ?? 10) / 10;
          audioNode.playbackRate = parseRate(slide.audioPlaybackRate);

          const sourceNode = audioCtx.createMediaElementSource(audioNode);
          sourceNode.connect(audioDest);
          audioNode.play().catch(() => {});
        } catch {
          /* ignore audio connect error */
        }
      }

      // Setup video playback if present
      if (videoNode) {
        try {
          videoNode.currentTime = slide.videoClips?.[0]?.start ?? 0;
          videoNode.volume = (slide.videoVolume ?? 10) / 10;
          videoNode.playbackRate = parseRate(slide.videoPlaybackRate);
          videoNode.play().catch(() => {});
        } catch {
          /* ignore */
        }
      }

      const slideStartTime = performance.now();

      // Real-time animation loop for current slide duration
      while (true) {
        if (signal?.aborted) throw new Error('Экспорт отменён пользователем');

        const now = performance.now();
        const slideElapsedSec = (now - slideStartTime) / 1000;
        if (slideElapsedSec >= slideDurationSec) break;

        const totalElapsedSec = accumulatedTimeSec + slideElapsedSec;
        const currentPercent = Math.min(98, Math.round(10 + (totalElapsedSec / totalDurationSec) * 88));

        onProgress?.({
          currentSlide: i + 1,
          totalSlides,
          percent: currentPercent,
          statusText: `Запись слайда ${i + 1} из ${totalSlides} (${slideElapsedSec.toFixed(1)} / ${slideDurationSec.toFixed(1)}с)...`,
        });

        // 1. Draw Background
        ctx.fillStyle = slide.backgroundColor || '#1a1916';
        ctx.fillRect(0, 0, width, height);

        // 2. Draw Video Frame (directly from naturally playing video element)
        if (videoNode && !slide.coverBackground) {
          if (videoNode.readyState >= 2) {
            ctx.drawImage(videoNode, 0, 0, width, height);
          }
        }

        // 3. Draw Image
        if (loadedImg && !slide.coverBackground) {
          const imgW = loadedImg.naturalWidth || width;
          const imgH = loadedImg.naturalHeight || height;
          const scale = Math.min(width / imgW, height / imgH);
          const drawW = imgW * scale;
          const drawH = imgH * scale;
          const drawX = (width - drawW) / 2;
          const drawY = (height - drawH) / 2;
          ctx.drawImage(loadedImg, drawX, drawY, drawW, drawH);
        }

        // 4. Cover background overlay
        if (slide.coverBackground && slide.backgroundColor) {
          ctx.fillStyle = slide.backgroundColor;
          ctx.fillRect(0, 0, width, height);
        }

        // 5. Primary Text
        const textContent = (slide.text || '').replace(/<\/br>/g, '\n');
        const plainText = stripHtml(textContent);
        if (plainText) {
          const textSizeVh = slide.textSize ?? 14;
          const fontSizePx = Math.round((textSizeVh / 100) * height);
          ctx.font = `bold ${fontSizePx}px Inter, Roboto, sans-serif`;
          ctx.fillStyle = slide.textColor || '#ffffff';

          const maxTextW = width * 0.85;
          const lines = wrapText(ctx, plainText, maxTextW);

          const pos = slide.textPosition || 'center';
          let align: CanvasTextAlign = 'center';
          let startX = width / 2;
          if (pos === 'left') { align = 'left'; startX = width * 0.08; }
          else if (pos === 'right') { align = 'right'; startX = width * 0.92; }

          ctx.textAlign = align;
          ctx.textBaseline = 'middle';

          const lineHeightPx = fontSizePx * 1.35;
          const totalTextH = lines.length * lineHeightPx;
          let startY = (height - totalTextH) / 2 + lineHeightPx / 2;

          if (pos === 'top') startY = height * 0.12 + lineHeightPx / 2;
          else if (pos === 'bottom') startY = height * 0.85 - totalTextH + lineHeightPx / 2;

          if (slide.hasBackgroundColor) {
            const boxPadding = 16;
            ctx.fillStyle = slide.textBackgroundColor || 'rgba(0,0,0,0.6)';
            lines.forEach((l, idx) => {
              const m = ctx.measureText(l);
              const lW = m.width + boxPadding * 2;
              let lX = startX - lW / 2;
              if (align === 'left') lX = startX - boxPadding;
              else if (align === 'right') lX = startX - lW + boxPadding;
              const lY = startY + idx * lineHeightPx - fontSizePx / 2 - boxPadding / 2;
              ctx.fillRect(lX, lY, lW, fontSizePx + boxPadding);
            });
            ctx.fillStyle = slide.textColor || '#ffffff';
          }

          lines.forEach((l, idx) => {
            const lY = startY + idx * lineHeightPx;
            // Draw dark outline text-shadow for white / light text
            ctx.shadowColor = 'rgba(0,0,0,0.85)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 2;
            ctx.fillText(l, startX, lY);
            ctx.shadowColor = 'transparent';
          });
        }

        // 6. Sub-text
        const subTextPlain = (slide.subText || '').trim();
        if (subTextPlain) {
          const subSizeVh = slide.subTextSize ?? 6;
          const subFontSizePx = Math.round((subSizeVh / 100) * height);
          ctx.font = `${subFontSizePx}px Inter, Roboto, sans-serif`;
          ctx.fillStyle = slide.subTextColor || '#cccccc';

          const subPos = slide.subTextPosition || 'bottom';
          let subAlign: CanvasTextAlign = 'center';
          let subX = width / 2;
          if (subPos === 'left') { subAlign = 'left'; subX = width * 0.08; }
          else if (subPos === 'right') { subAlign = 'right'; subX = width * 0.92; }

          ctx.textAlign = subAlign;
          ctx.textBaseline = 'middle';
          let subY = height * 0.92;
          if (subPos === 'top') subY = height * 0.08;

          ctx.shadowColor = 'rgba(0,0,0,0.85)';
          ctx.shadowBlur = 6;
          ctx.fillText(subTextPlain, subX, subY);
          ctx.shadowColor = 'transparent';
        }

        // 7. Plug slide indicator
        if (slide.isPlug) {
          ctx.fillStyle = 'rgba(0,0,0,0.5)';
          ctx.fillRect(0, 0, width, height);
          ctx.font = 'bold 24px Inter, sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.textAlign = 'center';
          ctx.fillText('ЗАГЛУШКА', width / 2, height / 2);
        }

        // Wait for next frame interval
        await new Promise(r => setTimeout(r, Math.max(10, Math.floor(frameIntervalMs))));
      }

      if (audioNode) {
        audioNode.pause();
        audioNode.src = '';
      }
      if (videoNode) {
        videoNode.pause();
      }

      accumulatedTimeSec += slideDurationSec;
    }

    onProgress?.({
      currentSlide: totalSlides,
      totalSlides,
      percent: 99,
      statusText: 'Финализация видеофайла...',
    });

    // Stop recording and compile final Blob
    await new Promise<void>(resolve => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    const isMp4 = selectedMime.includes('mp4');
    const ext = isMp4 ? 'mp4' : 'webm';
    const blob = new Blob(chunks, { type: selectedMime });

    // Download video file
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const cleanName = (treadmillName && treadmillName !== 'Тредмил' ? treadmillName : 'Тредмил')
      .replace(/[\\/:*?"<>|]/g, '_')
      .trim();
    link.href = url;
    link.download = `${cleanName}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);

    onProgress?.({
      currentSlide: totalSlides,
      totalSlides,
      percent: 100,
      statusText: '✅ Видео успешно загружено!',
    });

  } finally {
    try {
      await audioCtx.close();
    } catch {
      /* ignore */
    }
  }
}
